import React, { useState, useEffect, ChangeEvent } from 'react'
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import { InfinitySpin } from 'react-loader-spinner'
import { useSelector } from 'react-redux';
import { selectIsLoading } from '../../store/features/transmissionSlice';
import SpeechButton from '../common/SpeechButton';

const filter = createFilterOptions<string>();

interface TransmissionInputProps {
  handleSendClick: (inputValue: string | null) => void
}

const TransmissionInput: React.FC<TransmissionInputProps> = ({ handleSendClick }) => {
  const isLoading = useSelector(selectIsLoading)
  const [value, setValue] = useState<string | null>('');
  const [questionOptions, setQuestionOptions] = useState<string[]>([]);
  const [clearTranscriptFn, setClearTranscriptFn] = useState<(() => void) | null>(null);

  const handleSpeechResult = (text: string) => {
    setValue(text);
  };

  const handleSpeechPartialResult = (text: string) => {
    setValue(text);
  };

  // Add a function to clear the input when starting speech
  const handleStartListening = () => {
    setValue(''); // Clear the input field
  };

  // Handle setting the clearTranscript function from SpeechButton
  const handleClearTranscript = (clearTranscript: () => void) => {
    setClearTranscriptFn(() => clearTranscript);
  };

  const handleValueChange = (newValue: string | null) => {
    setValue(newValue);
    // Don't clear transcript on regular value changes
  };

  useEffect(() => {
    const loadQuestions = () => {
      const _questions = localStorage.getItem('transmission') || '[]'
      setQuestionOptions(JSON.parse(_questions))
    }
    setValue('')
    loadQuestions()
  }, [])

  if (isLoading)
    return (<Grid item xs={12} sx={{ textAlign: 'center', color: '#000' }}>
      <InfinitySpin
        width="200"
        color="#4fa94d"
      /> <br />
      {value}
    </Grid>)
  
  return (
    <Grid item xs={4} sx={{ px: 2, position: 'fixed', bottom: '15px', right: '15px', width: '600px' }}>
      <Autocomplete
        fullWidth
        value={value}
        onChange={(event: any, newValue) => {
          const previousValue = value;
          setValue(newValue);

          if (previousValue && (newValue === null || newValue === undefined || newValue === '') && clearTranscriptFn) {
            clearTranscriptFn();
          }

          if (event?.key === 'Enter' && newValue?.trim()) {
            const _indexFound = questionOptions.findIndex(r => r === newValue)
            if (_indexFound === -1)
              setQuestionOptions((v) => [newValue, ...questionOptions])

            handleSendClick(newValue)
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;

          const isExisting = options.some((option) => inputValue === option);
          if (inputValue !== '' && !isExisting) {
            filtered.push(inputValue);
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        id="transmission-question"
        options={questionOptions}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }

          if (option) {
            return option;
          }
          return option;
        }}
        renderOption={(props, option) => <li {...props}>{option}</li>}
        sx={{ mt: 1 }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Ask something about the transmission system ..."
            sx={{ color: '#fff', backgroundColor: '#fff', width: '80wv' }}
            onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              const newValue = event?.target.value
              handleValueChange(newValue)
              
              // Clear transcript when user manually clears the input
              if (!newValue && clearTranscriptFn) {
                clearTranscriptFn();
              }
            }}
            onKeyDown={(event: any) => {
              if (event?.key === 'Enter') {
                const _indexFound = questionOptions.findIndex(r => r === value)
                if (_indexFound === -1 && value)
                  setQuestionOptions((v) => [value, ...questionOptions])

                handleSendClick(value)
              }
            }}
          />
        )}
      />
    </Grid>
  )
}

export default TransmissionInput 